import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Shield, Plus, Users, CheckCircle, XCircle, AlertTriangle, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const texts = {
  es: {
    title: 'Gestión de Consentimiento',
    subtitle: 'Configurar avisos de privacidad por empresa',
    selectCompany: 'Seleccionar Empresa',
    consentEnabled: 'Consentimiento habilitado',
    consentDisabled: 'Consentimiento deshabilitado',
    jurisdiction: 'Jurisdicción',
    createNotice: 'Crear Aviso de Privacidad',
    titleEs: 'Título (Español)',
    titleEn: 'Título (Inglés)',
    bodyEs: 'Contenido (Español)',
    bodyEn: 'Contenido (Inglés)',
    save: 'Guardar',
    cancel: 'Cancelar',
    active: 'Activo',
    inactive: 'Inactivo',
    version: 'Versión',
    totalEmployees: 'Total Empleados',
    consented: 'Con Consentimiento',
    pending: 'Pendientes',
    revoked: 'Revocados',
    noNotice: 'No hay aviso de privacidad configurado para esta empresa',
    enableFirst: 'Activa el consentimiento y crea un aviso de privacidad',
    notices: 'Avisos de Privacidad',
    configSaved: 'Configuración guardada',
    noticeSaved: 'Aviso de privacidad creado correctamente'
  },
  en: {
    title: 'Consent Management',
    subtitle: 'Configure privacy notices per company',
    selectCompany: 'Select Company',
    consentEnabled: 'Consent enabled',
    consentDisabled: 'Consent disabled',
    jurisdiction: 'Jurisdiction',
    createNotice: 'Create Privacy Notice',
    titleEs: 'Title (Spanish)',
    titleEn: 'Title (English)',
    bodyEs: 'Content (Spanish)',
    bodyEn: 'Content (English)',
    save: 'Save',
    cancel: 'Cancel',
    active: 'Active',
    inactive: 'Inactive',
    version: 'Version',
    totalEmployees: 'Total Employees',
    consented: 'Consented',
    pending: 'Pending',
    revoked: 'Revoked',
    noNotice: 'No privacy notice configured for this company',
    enableFirst: 'Enable consent and create a privacy notice',
    notices: 'Privacy Notices',
    configSaved: 'Configuration saved',
    noticeSaved: 'Privacy notice created successfully'
  }
};

const ConsentManagement = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = texts[language] || texts.es;

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [consentStatus, setConsentStatus] = useState(null);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newNotice, setNewNotice] = useState({ title_es: 'Aviso de Privacidad', title_en: 'Privacy Notice', body_es: '', body_en: '', jurisdiction: 'LFPDPPP' });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadConsentStatus(selectedCompany);
      loadNotices(selectedCompany);
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await axios.get('/admin/companies');
      if (response.data?.success) {
        setCompanies(response.data.data || []);
      }
    } catch (err) {
      logger.error('Error loading companies:', err);
    }
  };

  const loadConsentStatus = async (empresaId) => {
    setLoading(true);
    try {
      const response = await axios.get(`/consent/empresa/${empresaId}/status`);
      if (response.data?.success) {
        setConsentStatus(response.data.data);
      }
    } catch (err) {
      logger.error('Error loading consent status:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadNotices = async (empresaId) => {
    try {
      const response = await axios.get(`/consent/notices/${empresaId}`);
      if (response.data?.success) {
        setNotices(response.data.data || []);
      }
    } catch (err) {
      logger.error('Error loading notices:', err);
    }
  };

  const toggleConsent = async (enabled) => {
    try {
      await axios.put(`/consent/empresa/${selectedCompany}/toggle`, {
        consent_activo: enabled,
        jurisdiction: consentStatus?.jurisdiction || 'LFPDPPP'
      });
      toast.success(t.configSaved);
      loadConsentStatus(selectedCompany);
    } catch (err) {
      toast.error('Error');
    }
  };

  const updateJurisdiction = async (jurisdiction) => {
    try {
      await axios.put(`/consent/empresa/${selectedCompany}/toggle`, {
        consent_activo: consentStatus?.consent_activo || false,
        jurisdiction
      });
      toast.success(t.configSaved);
      loadConsentStatus(selectedCompany);
    } catch (err) {
      toast.error('Error');
    }
  };

  const handleCreateNotice = async () => {
    try {
      await axios.post(`/consent/notices/${selectedCompany}`, {
        ...newNotice,
        jurisdiction: consentStatus?.jurisdiction || 'LFPDPPP'
      });
      toast.success(t.noticeSaved);
      setDialogOpen(false);
      setNewNotice({ title_es: 'Aviso de Privacidad', title_en: 'Privacy Notice', body_es: '', body_en: '', jurisdiction: 'LFPDPPP' });
      loadNotices(selectedCompany);
      loadConsentStatus(selectedCompany);
    } catch (err) {
      toast.error('Error');
    }
  };

  if (user?.rol !== 'super_admin') return null;

  return (
    <div className="space-y-6" data-testid="consent-management">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-blue-600" />
        <div>
          <h2 className="text-xl font-semibold">{t.title}</h2>
          <p className="text-sm text-gray-500">{t.subtitle}</p>
        </div>
      </div>

      {/* Company Selector */}
      <Card>
        <CardContent className="pt-4">
          <Label>{t.selectCompany}</Label>
          <Select value={selectedCompany || ''} onValueChange={setSelectedCompany}>
            <SelectTrigger data-testid="consent-company-select">
              <SelectValue placeholder={t.selectCompany} />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {c.nombre}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedCompany && consentStatus && (
        <>
          {/* Config */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{language === 'es' ? 'Configuración' : 'Configuration'}</span>
                <Badge variant={consentStatus.consent_activo ? 'default' : 'secondary'}>
                  {consentStatus.consent_activo ? t.consentEnabled : t.consentDisabled}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>{language === 'es' ? 'Requerir consentimiento' : 'Require consent'}</Label>
                <Switch
                  checked={consentStatus.consent_activo}
                  onCheckedChange={toggleConsent}
                  data-testid="consent-toggle"
                />
              </div>
              <div>
                <Label>{t.jurisdiction}</Label>
                <Select value={consentStatus.jurisdiction} onValueChange={updateJurisdiction}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LFPDPPP">LFPDPPP (Mexico)</SelectItem>
                    <SelectItem value="PDPA">PDPA (Singapore)</SelectItem>
                    <SelectItem value="GDPR">GDPR (Europe)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 text-center">
                <Users className="h-5 w-5 mx-auto mb-1 text-gray-500" />
                <p className="text-2xl font-bold">{consentStatus.stats?.total_employees || 0}</p>
                <p className="text-xs text-gray-500">{t.totalEmployees}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-500" />
                <p className="text-2xl font-bold text-green-600">{consentStatus.stats?.consented || 0}</p>
                <p className="text-xs text-gray-500">{t.consented}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-amber-500" />
                <p className="text-2xl font-bold text-amber-600">{consentStatus.stats?.pending || 0}</p>
                <p className="text-xs text-gray-500">{t.pending}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <XCircle className="h-5 w-5 mx-auto mb-1 text-red-500" />
                <p className="text-2xl font-bold text-red-600">{consentStatus.stats?.revoked || 0}</p>
                <p className="text-xs text-gray-500">{t.revoked}</p>
              </CardContent>
            </Card>
          </div>

          {/* Notices */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{t.notices}</span>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="create-notice-btn">
                      <Plus className="h-4 w-4 mr-1" /> {t.createNotice}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{t.createNotice}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{t.titleEs}</Label>
                          <Input value={newNotice.title_es} onChange={e => setNewNotice({...newNotice, title_es: e.target.value})} />
                        </div>
                        <div>
                          <Label>{t.titleEn}</Label>
                          <Input value={newNotice.title_en} onChange={e => setNewNotice({...newNotice, title_en: e.target.value})} />
                        </div>
                      </div>
                      <div>
                        <Label>{t.bodyEs}</Label>
                        <textarea 
                          className="w-full min-h-[200px] p-3 border rounded-md text-sm" 
                          value={newNotice.body_es}
                          onChange={e => setNewNotice({...newNotice, body_es: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>{t.bodyEn}</Label>
                        <textarea 
                          className="w-full min-h-[200px] p-3 border rounded-md text-sm"
                          value={newNotice.body_en}
                          onChange={e => setNewNotice({...newNotice, body_en: e.target.value})}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>{t.cancel}</Button>
                      <Button onClick={handleCreateNotice} disabled={!newNotice.body_es} data-testid="save-notice-btn">{t.save}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {notices.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>{t.noNotice}</p>
                  <p className="text-xs">{t.enableFirst}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {notices.map(n => (
                    <div key={n.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium">{language === 'en' && n.title_en ? n.title_en : n.title_es}</span>
                        <span className="text-xs text-gray-400 ml-2">{t.version} {n.version}</span>
                      </div>
                      <Badge variant={n.is_active ? 'default' : 'secondary'}>
                        {n.is_active ? t.active : t.inactive}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ConsentManagement;
